<!--
  Form View Render Template
  =========================
  
  Dynamic HTML template that renders form data based on processed metadata and step definitions.
  This template works in conjunction with the FormRenderComponent to programmatically generate
  form displays with consistent structure between UI viewing and PDF generation.
  
  Template Structure:
  - Structured View: When form steps are defined, renders hierarchical step → section → field layout
  - WordPress Fields: Legacy field support for backward compatibility
  - Orphaned Fields: Fields not belonging to any defined step structure
  - Reusable Templates: Field and WordPress field templates for consistent rendering
  
  Layout Features:
  - Responsive grid system with 2-column layout on medium+ screens
  - Alternating background colors for visual separation
  - Conditional rendering based on field values and metadata
  - Support for various field types (text, date, attachments, signatures, checkboxes)
  
  Conditional Rendering Logic:
  - hasFormSteps: Determines structured vs flat field layout
  - step.hasValues: Shows/hides steps with no data
  - section.hasValues: Shows/hides sections with no data
  - field types: Different rendering for different field types
  
  @author MacSafety Development Team
  @version 1.0
-->

<!-- 
  STRUCTURED FORM DISPLAY
  =======================
  Renders forms with defined step structure when formSteps are available.
  Creates hierarchical layout: Step → Section → Field/Checkbox Group
  Only displays when both formEntry exists and hasFormSteps is true.
-->
<ng-container *ngIf="formEntry && hasFormSteps" class="structured-view">
  <!-- 
    STEP RENDERING LOOP
    ===================
    Iterates through each processed step, creating a card for each step.
    Each step contains sections, which contain fields and checkbox groups.
  -->
  <ion-card
    *ngFor="let step of processedStepsData"
    class="step-card no-card-style ignore-dark-mode"
  >
    <!-- 
      STEP HEADER
      ===========
      Displays step title with associated icon.
      Uses light color theme for consistent visual hierarchy.
    -->
    <ion-card-header class="step-header ignore-dark-mode">
      <ion-card-title color="light" class="ignore-dark-mode">
        <ion-icon [name]="step.icon"></ion-icon>
        {{ step.title }}
      </ion-card-title>
    </ion-card-header>

    <!-- 
      STEP CONTENT AREA
      =================
      Contains all sections within this step.
      Handles both empty steps and steps with content.
    -->
    <ion-card-content class="step-content">
      <!-- 
        EMPTY STEP HANDLING
        ===================
        Shows message when step has no values/data.
        Prevents empty steps from cluttering the interface.
      -->
      <div *ngIf="!step.hasValues" class="no-answers">
        <ion-text color="medium">
          <p>No answers provided in this section</p>
        </ion-text>
      </div>

      <!-- 
        SECTION RENDERING
        =================
        Only renders sections when step has values and sections exist.
        Each section can contain fields and checkbox groups.
      -->
      <ng-container
        *ngIf="step.sections && step.sections.length > 0 && step.hasValues"
      >
        <ng-container
          *ngFor="let section of step.sections; let sectionIndex = index"
          class="section-container"
        >
          <!-- 
            SECTION VALUE CHECK
            ===================
            Only renders sections that contain actual data.
            Filters out empty sections for cleaner display.
          -->
          <ng-container *ngIf="section.hasValues">
            <!-- 
              SECTION HEADER
              ===============
              Optional section title and subtitle display.
              Provides context for section content when defined.
            -->
            <ion-text
              *ngIf="section.title || section.subtitle"
              class="section-header ignore-dark-mode"
            >
              <h3 *ngIf="section.title" class="section-title">
                {{ section.title }}
              </h3>
              <h4 *ngIf="section.subtitle" class="section-subtitle">
                {{ section.subtitle }}
              </h4>
            </ion-text>

            <!-- 
  SECTION INFO BLOCKS
  ====================
  Static informational content defined in the form step configuration.
  Rendered before the field grid. Supports paragraphs and regulatory cards.
-->
            <ng-container
              *ngIf="section.infoBlocks && section.infoBlocks.length > 0"
            >
              <ng-container *ngFor="let block of section.infoBlocks">
                <!-- PARAGRAPH BLOCK -->
                <p
                  *ngIf="block.type === 'paragraph'"
                  class="info-block-paragraph"
                >
                  {{ block.content }}
                </p>

                <!-- REGULATORY CARD BLOCK -->
                <ion-card
                  *ngIf="block.type === 'regulatoryCard'"
                  class="information-card regulatory-info-card"
                >
                  <ion-card-header>
                    <ion-card-title>
                      <ion-icon name="information-circle-outline"></ion-icon
                      >&nbsp;{{ block.title }}
                    </ion-card-title>
                    <ion-card-subtitle *ngIf="block.subtitle">{{
                      block.subtitle
                    }}</ion-card-subtitle>
                  </ion-card-header>
                  <ion-card-content>
                    <p *ngIf="block.listTopper" class="regulatory-list-topper">{{ block.listTopper }}</p>
                    <ol class="regulatory-list">
                      <li *ngFor="let item of block.listItems">
                        <span [innerHTML]="item.text"></span>
                        <ul
                          *ngIf="item.subItems && item.subItems.length > 0"
                          class="regulatory-sublist"
                        >
                          <li
                            *ngFor="let subItem of item.subItems"
                            [innerHTML]="subItem"
                          ></li>
                        </ul>
                      </li>
                    </ol>
                  </ion-card-content>
                </ion-card>
              </ng-container>
            </ng-container>

            <!-- 
              SECTION CONTENT CARD
              =====================
              Wraps section content in a card for visual separation.
              Contains the field grid with fields and checkbox groups.
            -->
            <ion-card class="section-card no-card-style">
              <ion-card-content
                class="section-card-content no-card-content-style"
              >
                <!-- 
                  FIELD GRID SYSTEM
                  ==================
                  Responsive grid that displays fields in pairs.
                  Combines regular fields and checkbox groups seamlessly.
                  Creates 2-column layout on medium+ screens, 1 column on mobile.
                -->
                <ion-grid class="field-grid">
                  <!-- 
                    ITEM ITERATION
                    ===============
                    Loops through combined section items (fields + checkbox groups).
                    Only creates rows for even indices (0, 2, 4...) to create pairs.
                    Each row contains 2 items side by side.
                  -->
                  <ng-container
                    *ngFor="
                      let item of getCombinedSectionItems(
                        section,
                        sectionIndex
                      );
                      let i = index
                    "
                  >
                    <ion-row *ngIf="i % 2 === 0" class="field-row print-row">
                      <!-- 
                        FIRST ITEM (LEFT COLUMN)
                        =========================
                        Always displays the current item (index i).
                        Handles both field types and checkbox group types.
                      -->
                      <ion-col size="12" size-md="6" class="field-col col-odd">
                        <!-- 
                          REGULAR FIELD RENDERING
                          =======================
                          Renders individual form fields using the field template.
                          Passes field data to the reusable template.
                        -->
                        <ng-container *ngIf="item.type === 'field'">
                          <ng-container
                            *ngTemplateOutlet="
                              fieldTemplate;
                              context: { field: item.data }
                            "
                          ></ng-container>
                        </ng-container>

                        <!-- 
                          CHECKBOX GROUP RENDERING
                          ========================
                          Renders checkbox groups with checked items display.
                          Shows group title and list of selected checkboxes.
                        -->
                        <ng-container *ngIf="item.type === 'checkboxGroup'">
                          <!-- 
                            CHECKED CHECKBOXES DISPLAY
                            =========================
                            Shows selected checkboxes when any exist.
                            Displays group title and list of checked items.
                          -->
                          <div
                            *ngIf="item.data.hasCheckedFields"
                            class="checkbox-group-container"
                          >
                            <!-- Checkbox group title -->
                            <div class="checkbox-group-title">
                              <ion-text color="medium">
                                {{ item.data.title }}
                              </ion-text>
                            </div>

                            <!-- 
                              CHECKED ITEMS LIST
                              ===================
                              Compact grid showing all checked checkboxes.
                              Uses success icon and bullet list for clarity.
                            -->
                            <div class="checkbox-list">
                              <ul class="selected-items-list">
                                <li
                                  *ngFor="let field of item.data.checkedFields"
                                  class="selected-item"
                                >
                                  <ion-icon
                                    name="checkmark-circle"
                                    color="success"
                                    size="small"
                                  ></ion-icon>
                                  <span>{{ field.label }}</span>
                                </li>
                              </ul>
                            </div>
                          </div>

                          <!-- 
                            NO SELECTION DISPLAY
                            ====================
                            Shows "none selected" when no checkboxes are checked.
                            Maintains consistent layout even with empty groups.
                          -->
                          <div
                            *ngIf="!item.data.hasCheckedFields"
                            class="checkbox-group-container"
                          >
                            <div class="checkbox-group-title">
                              <ion-text color="medium">
                                {{ item.data.title }}
                              </ion-text>
                            </div>
                            <div class="no-selection">
                              <ion-text color="medium">
                                None selected
                              </ion-text>
                            </div>
                          </div>
                        </ng-container>
                      </ion-col>

                      <!-- 
                        SECOND ITEM (RIGHT COLUMN)
                        ==========================
                        Displays the next item (index i + 1) if it exists.
                        Uses ngSwitch to handle different item types.
                        Only renders when there's a second item to display.
                      -->
                      <ion-col
                        *ngIf="
                          getCombinedSectionItems(section, sectionIndex)[i + 1]
                        "
                        size="12"
                        size-md="6"
                        class="field-col col-even"
                      >
                        <!-- 
                          ITEM TYPE SWITCHING
                          ====================
                          Determines rendering method based on item type.
                          Handles both field and checkbox group types.
                        -->
                        <ng-container
                          [ngSwitch]="
                            getCombinedSectionItems(section, sectionIndex)[
                              i + 1
                            ].type
                          "
                        >
                          <!-- Regular field rendering for second item -->
                          <ng-container *ngSwitchCase="'field'">
                            <ng-container
                              *ngTemplateOutlet="
                                fieldTemplate;
                                context: {
                                  field: getCombinedSectionItems(
                                    section,
                                    sectionIndex
                                  )[i + 1].data,
                                }
                              "
                            ></ng-container>
                          </ng-container>

                          <!-- Checkbox group rendering for second item -->
                          <ng-container *ngSwitchCase="'checkboxGroup'">
                            <!-- 
                              CHECKED CHECKBOXES DISPLAY (SECOND ITEM)
                              ======================================
                              Same logic as first item but for the second column.
                              Shows selected checkboxes when any exist.
                            -->
                            <div
                              *ngIf="
                                getCombinedSectionItems(section, sectionIndex)[
                                  i + 1
                                ].data.hasCheckedFields
                              "
                              class="checkbox-group-container"
                            >
                              <!-- Checkbox group title -->
                              <div class="checkbox-group-title">
                                <ion-text color="medium">
                                  {{
                                    getCombinedSectionItems(
                                      section,
                                      sectionIndex
                                    )[i + 1].data.title
                                  }}
                                </ion-text>
                              </div>

                              <!-- Checked items list with compact grid -->
                              <div class="checkbox-list">
                                <ul class="selected-items-list">
                                  <li
                                    *ngFor="
                                      let field of getCombinedSectionItems(
                                        section,
                                        sectionIndex
                                      )[i + 1].data.checkedFields
                                    "
                                    class="selected-item"
                                  >
                                    <ion-icon
                                      name="checkmark-circle"
                                      color="success"
                                      size="small"
                                    ></ion-icon>
                                    <span>{{ field.label }}</span>
                                  </li>
                                </ul>
                              </div>
                            </div>

                            <!-- No selection display for second item -->
                            <div
                              *ngIf="
                                !getCombinedSectionItems(section, sectionIndex)[
                                  i + 1
                                ].data.hasCheckedFields
                              "
                              class="checkbox-group-container"
                            >
                              <div class="checkbox-group-title">
                                <ion-text color="medium">
                                  {{
                                    getCombinedSectionItems(
                                      section,
                                      sectionIndex
                                    )[i + 1].data.title
                                  }}
                                </ion-text>
                              </div>
                              <div class="no-selection">
                                <ion-text color="medium">
                                  None selected
                                </ion-text>
                              </div>
                            </div>
                          </ng-container>
                        </ng-container>
                      </ion-col>
                    </ion-row>
                  </ng-container>
                </ion-grid>
              </ion-card-content>
            </ion-card>
          </ng-container>
        </ng-container>
      </ng-container>
    </ion-card-content>
  </ion-card>

  <!-- 
    WORDPRESS FIELDS SECTION
    =========================
    Legacy support for WordPress-specific field data.
    Renders as a separate step card for backward compatibility.
    Only displays when WordPress fields exist and have data.
  -->
  <ion-card
    *ngIf="wordPressFields && wordPressFields.length > 0"
    class="step-card no-card-style ignore-dark-mode"
  >
    <!-- WordPress fields step header -->
    <ion-card-header class="step-header ignore-dark-mode">
      <ion-card-title color="light" class="ignore-dark-mode">
        <ion-icon name="information-circle-outline"></ion-icon>
        Legacy WordPress Fields
      </ion-card-title>
    </ion-card-header>

    <!-- WordPress fields content -->
    <ion-card-content class="step-content">
      <!-- 
        WORDPRESS FIELDS GRID
        ======================
        Similar grid system to regular fields but for WordPress data.
        Uses WordPress field template for consistent rendering.
      -->
      <ion-grid class="field-grid">
        <ng-container *ngFor="let field of wordPressFields; let i = index">
          <ion-row *ngIf="i % 2 === 0" class="field-row print-row">
            <!-- First WordPress field -->
            <ion-col
              size="12"
              size-md="6"
              class="field-col"
              [class.col-even]="shouldHaveEvenBackground(i)"
              [class.col-odd]="shouldHaveOddBackground(i)"
            >
              <ng-container
                *ngTemplateOutlet="
                  wordPressFieldTemplate;
                  context: { field: field }
                "
              ></ng-container>
            </ion-col>
            <!-- Second WordPress field (if exists) -->
            <ion-col
              size="12"
              size-md="6"
              class="field-col"
              [class.col-even]="shouldHaveEvenBackground(i + 1)"
              [class.col-odd]="shouldHaveOddBackground(i + 1)"
            >
              <ng-container
                *ngTemplateOutlet="
                  wordPressFieldTemplate;
                  context: { field: wordPressFields[i + 1] }
                "
              ></ng-container>
            </ion-col>
          </ion-row>
        </ng-container>
      </ion-grid>
    </ion-card-content>
  </ion-card>
</ng-container>

<!-- 
  ORPHANED FIELDS DISPLAY
  ========================
  Renders fields that don't belong to any defined step structure.
  These are typically additional form data or legacy fields.
  Only displays when orphaned fields exist.
-->
<ion-card *ngIf="processedOrphanedFields.length > 0" class="step-card">
  <ion-card-header class="step-header">
    <ion-card-title color="light">
      <ion-icon name="information-circle-outline"></ion-icon>
      Other Form Data
    </ion-card-title>
  </ion-card-header>

  <ion-card-content class="step-content">
    <!-- 
      ORPHANED FIELDS GRID
      =====================
      Grid system for fields not in structured steps.
      Uses same 2-column responsive layout as structured fields.
    -->
    <ion-grid class="field-grid">
      <ng-container
        *ngFor="let field of processedOrphanedFields; let i = index"
      >
        <ion-row *ngIf="i % 2 === 0" class="field-row print-row">
          <!-- First orphaned field -->
          <ion-col
            size="12"
            size-md="6"
            class="field-col"
            [class.col-even]="shouldHaveEvenBackground(i)"
            [class.col-odd]="shouldHaveOddBackground(i)"
          >
            <ng-container
              *ngTemplateOutlet="fieldTemplate; context: { field: field }"
            ></ng-container>
          </ion-col>

          <!-- Second orphaned field (if exists) -->
          <ion-col
            *ngIf="processedOrphanedFields[i + 1]"
            size="12"
            size-md="6"
            class="field-col"
            [class.col-even]="shouldHaveEvenBackground(i + 1)"
            [class.col-odd]="shouldHaveOddBackground(i + 1)"
          >
            <ng-container
              *ngTemplateOutlet="
                fieldTemplate;
                context: { field: processedOrphanedFields[i + 1] }
              "
            ></ng-container>
          </ion-col>
        </ion-row>
      </ng-container>
    </ion-grid>
  </ion-card-content>
</ion-card>

<!-- 
  TEMPLATE DEFINITIONS
  ====================
  Reusable templates for consistent field rendering across the application.
  These templates handle different field types and data structures.
-->

<!-- 
  WORDPRESS FIELD TEMPLATE
  =========================
  Template for rendering WordPress-specific field data.
  Displays field label and value in simple format.
-->
<ng-template #wordPressFieldTemplate let-field="field">
  <div class="field-pair">
    <ion-label class="field-label">{{ field.fieldLabel }}</ion-label>
    <div class="field-value">
      <span>{{ field.fieldValue }}</span>
    </div>
  </div>
</ng-template>

<!-- 
  MAIN FIELD TEMPLATE
  ====================
  Comprehensive template for rendering all form field types.
  Handles text, date, attachment, and signature field types.
  Provides consistent layout and interaction across field types.
-->
<ng-template #fieldTemplate let-field="field">
  <div class="field-pair">
    <!-- Field label display -->
    <ion-label class="field-label">{{ field.label }}</ion-label>

    <!-- Field value display with type-specific rendering -->
    <div class="field-value">
      <!-- 
        DATE FIELD RENDERING
        ====================
        Special formatting for date fields.
        Uses different date formats for submission date vs other dates.
      -->
      <span *ngIf="field.isDate">
        {{
          field.value
            | date
              : (field.label === "submissionDate"
                  ? "MMM d, y, h:mm a"
                  : "MMM d, y")
        }}
      </span>

      <!-- 
        REGULAR TEXT FIELD RENDERING
        =============================
        Standard display for text, number, and other non-attachment fields.
        Only shows when field is not a date or attachment type.
      -->
      <span *ngIf="!field.isDate && !field.isAttachment">
        {{ field.value }}
      </span>

      <!-- 
        ATTACHMENT FIELD RENDERING
        ==========================
        Complex rendering for file uploads and signatures.
        Handles both upload arrays and signature arrays.
        Provides interactive elements for file viewing and download.
      -->
      <span *ngIf="field.isAttachment">
        <!-- 
          UPLOAD ATTACHMENTS DISPLAY
          ===========================
          Renders file uploads as interactive chips.
          Shows file type icons, names, and metadata.
          Handles placeholder attachments when files aren't resolved.
        -->
        <div class="attachment-chips-container" *ngIf="field.isUploadArray">
          <div
            class="chips-grid"
            *ngIf="getAttachmentData(field.key).length > 0"
          >
            <!-- 
              ATTACHMENT CHIP LOOP
              =====================
              Creates interactive chip for each attachment.
              Each chip shows file icon, name, type, and size.
              Clickable for file viewing/download.
            -->
            <ion-chip
              *ngFor="
                let attachment of getAttachmentData(field.key);
                let urlIndex = index
              "
              class="attachment-chip"
              [class.placeholder-chip]="
                !attachment.size && !attachment.fileType
              "
              (click)="
                onAttachmentClick(
                  attachment.url,
                  attachment.filename,
                  field.key
                )
              "
              [title]="attachment.filename"
            >
              <!-- 
                FILE TYPE ICON
                ===============
                Shows appropriate icon based on file type.
                Uses placeholder styling for unresolved attachments.
              -->
              <ion-icon
                *ngIf="attachment.fileType"
                [name]="getFileIcon(attachment.fileType, attachment.filename)"
                class="file-icon"
                [class.placeholder-icon]="
                  !attachment.size && !attachment.fileType
                "
              >
              </ion-icon>

              <!-- 
                ATTACHMENT METADATA
                ====================
                Displays filename and file details.
                Shows file type and size when available.
                Truncates long filenames for clean display.
              -->
              <ion-label class="chip-content">
                <span class="filename-truncated">{{
                  attachment.filename
                }}</span>
                <div
                  class="file-details"
                  *ngIf="attachment.size || attachment.fileType"
                >
                  <span class="file-type" *ngIf="attachment.fileType">{{
                    getFileDisplayName(attachment.fileType, attachment.filename)
                  }}</span>
                  <span class="file-size" *ngIf="attachment.size">
                    {{ attachment.size }}</span
                  >
                </div>
              </ion-label>
            </ion-chip>
          </div>
        </div>

        <!-- 
          SIGNATURE ATTACHMENTS DISPLAY
          =============================
          Renders digital signatures as clickable images.
          Shows signature preview with filename.
          Handles placeholder signatures when not resolved.
        -->
        <div class="signature-container" *ngIf="field.isSignatureArray">
          <div
            *ngFor="
              let attachment of getAttachmentData(field.key);
              let urlIndex = index
            "
            class="signature-container"
          >
            <!-- 
              SIGNATURE IMAGE
              ================
              Displays signature as clickable image.
              Uses placeholder styling for unresolved signatures.
              Clickable for signature viewing.
            -->
            <img
              [src]="attachment.url"
              [alt]="attachment.filename"
              [class]="
                isPlaceholderAttachment(attachment.url)
                  ? 'attachment-image attachment-placeholder'
                  : 'attachment-image'
              "
              (click)="
                onAttachmentClick(
                  attachment.url,
                  attachment.filename,
                  field.key
                )
              "
            />
            <!-- Signature filename display -->
            <div class="attachment-filename">{{ attachment.filename }}</div>
          </div>
        </div>

        <!-- 
          NO ATTACHMENTS MESSAGE
          ========================
          Shows message when field has no attachments.
          Provides context about what type of attachment is missing.
        -->
        <div
          *ngIf="getAttachmentData(field.key).length === 0"
          class="no-attachments-table"
        >
          No {{ getAttachmentFieldType(field.key) }}s available
        </div>
      </span>
    </div>
  </div>
</ng-template>
